<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue';
import DynamicForm from '@/views/DynamicForm.vue';
import {useRoute} from 'vue-router';
import clientService from '@/services/ClientService';
import {useI18n} from 'vue-i18n';
import InvestmentQuestionnaireService, {visibleInvestmentQuestionnaireResultKeys} from '@/services/InvestmentQuestionnaireService';
import {setButtonContentDefault, setButtonContentLoading, setButtonsContentSuccess, showSuccessMessage} from '@/helper';
import {authData} from '@/services/AuthService';
import DocumentService from "@/services/DocumentService";

const {t, te} = useI18n();
const props = defineProps({
  investmentQuestionnaire: {type: Object, required: false},
  disabled: {type: Boolean, required: false, default: false},
  prefillObject: {type: Object, required: false, default: null},
  clientId: {type: Number, required: true, default: null}
});

const params = useRoute().params;
const dynamicFormRef = ref(null);
const selectedForm = ref(null);
const form = ref({...props.investmentQuestionnaire});
const isOpen = ref(true);
const result = ref(null);
const emit = defineEmits(['confirm', 'cancel']);
const availableQuestionnaires = ref(null);
const availableContracts = ref([]);
const selectedContractId = ref(null);
const documentIsGenerating = ref(false);
// Inkrementuje se po dokončení re-fetche šablony — DynamicForm se remountuje
// až poté, co selectedForm obsahuje správný obsah (s novým číslem smlouvy).
const formKey = ref(0);
// Zabrání spuštění watcheru během inicializace v onMounted — selectedContractId se tam
// nastavuje z _meta, což by jinak přepsalo uložená data čistou šablonou.
const isInitialized = ref(false);

// Pro nové záznamy: zobrazit select jen když šablona obsahuje ${CLIENT_CONTRACT_NUMBER}.
// Pro editaci existujících: zobrazit vždy, pokud má klient nějaké smlouvy — uživatel
// může změnit přiřazenou smlouvu, která se použije při generování dokumentu.
const showContractSelect = computed(() =>
  !props.investmentQuestionnaire?.id && (
    selectedForm.value?.has_contract_number_keyword === true ||
    availableContracts.value.length > 0
  )
);

const closeModalWith = (value, state) => {
  form.value.data = dynamicFormRef.value?.config;
  // Uložit vybranou smlouvu do _meta — používá se při přegenerování dokumentu
  if (selectedContractId.value) {
    form.value.data = { ...(form.value.data ?? {}), _meta: { contract_id: selectedContractId.value } };
  }
  form.value.state = state;
  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      if (!state) {
        setButtonContentLoading('save-button');
        if (selectedForm.value.confirmation_required) {
          form.value.state = 'WAITING';
        } else {
          form.value.state = 'TO_REVIEW';
        }
      } else {
        setButtonContentLoading('concept-button');
      }
      emit('confirm', form.value);
    } else {
      emit('cancel');
    }
  }, 300);
};

function openModal() {
  isOpen.value = true;
}

onMounted(() => {
  if (props.investmentQuestionnaire?.result) {
    result.value = props.investmentQuestionnaire.result;
  }
  clientService.getAllActiveTemplates(params.id, 'INVESTMENT_QUESTIONNAIRE')
      .then(({ templates, contracts }) => {
        availableQuestionnaires.value = templates?.map(form => ({
          ...form.template,
          id: form.id,
          has_contract_number_keyword: form.has_contract_number_keyword ?? false,
        }));
        availableContracts.value = contracts ?? [];
        // Předvybrání první smlouvy jako výchozí hodnoty
        selectedContractId.value = contracts?.[0]?.id ?? null;

        if (props.investmentQuestionnaire?.data) {
          selectedForm.value = props.investmentQuestionnaire.data;
          // Obnovit uloženou smlouvu z _meta při editaci existujícího záznamu
          selectedContractId.value = props.investmentQuestionnaire.data?._meta?.contract_id ?? selectedContractId.value;
        } else {
          selectedForm.value = null;
        }
        // Aktivovat watcher až po nextTick — Vue odflushuje změny selectedContractId
        // z inicializace dříve, než se watcher může spustit s isInitialized=true.
        nextTick(() => { isInitialized.value = true; });
      });
});

/**
 * Dosadí uživatelem vyplněné hodnoty ze stávajícího formuláře do čerstvé šablony.
 * Šablona přináší nový text (nové číslo smlouvy v labelech), hodnoty odpovědí se zachovají.
 * Párování probíhá podle question.id.
 */
function mergeAnswersIntoTemplate(freshTemplate, currentForm) {
  if (!currentForm?.content || !freshTemplate?.content) return freshTemplate;
  const currentValues = {};
  for (const page of currentForm.content) {
    for (const question of page.questions ?? []) {
      if (question.id != null) currentValues[question.id] = question.value;
    }
  }
  return {
    ...freshTemplate,
    content: freshTemplate.content.map(page => ({
      ...page,
      questions: (page.questions ?? []).map(q => ({
        ...q,
        value: q.id in currentValues ? currentValues[q.id] : q.value,
      })),
    })),
  };
}

// Při změně smlouvy znovu načte všechny šablony s novým contract_id, aby se
// ${CLIENT_CONTRACT_NUMBER} nahradilo správnou hodnotou. Hodnoty vyplněné
// uživatelem se zachovají — nahradí se jen struktura (labely s číslem smlouvy).
watch(selectedContractId, (newContractId) => {
  if (!selectedForm.value || !isInitialized.value) return;
  const currentId = selectedForm.value.id;
  const currentForm = selectedForm.value;
  clientService.getAllActiveTemplates(params.id, 'INVESTMENT_QUESTIONNAIRE', newContractId)
    .then(({ templates }) => {
      availableQuestionnaires.value = templates?.map(t => ({
        ...t.template,
        id: t.id,
        has_contract_number_keyword: t.has_contract_number_keyword ?? false,
      }));
      const fresh = availableQuestionnaires.value?.find(t => t.id === currentId) ?? null;
      selectedForm.value = fresh ? mergeAnswersIntoTemplate(fresh, currentForm) : null;
      formKey.value++;
    });
});

function countResult() {
  setButtonContentLoading('count-button');
  const formValue = {
    ...form.value,
    data: dynamicFormRef.value?.config,
    client_id: props.clientId,
    creator_id: authData.loggedUser.id,
  };
  if(!formValue.state) {
    formValue.state = 'CONCEPT';
  }

  InvestmentQuestionnaireService.countResult(props.clientId, formValue.id, formValue).then(response => {
    result.value = response.questionnaire.result;
    form.value = {...response.questionnaire};
    setButtonContentDefault('count-button');
  });
}

function generateDocument() {
  documentIsGenerating.value = true;
  const body = {
    model_id: form.value.id,
    model_type: '\\App\\Models\\InvestmentQuestionnaire',
  }
  DocumentService.generateDocument(body).then(response => {
    documentIsGenerating.value = false;
    showSuccessMessage(t('general.document_generated_successfully'));
    emitter.emit('documentGenerated');
  })
}

/**
 * Returns the translated label for a result value key, falling back to the raw value.
 */
function translateResultValue(val) {
  if (val === null || val === undefined) return '-';
  const key = 'investmentQuestionnaireModal.' + val;
  return te(key) ? t(key) : String(val);
}

/**
 * Returns true when original_result exists and this key's value differs from the current result.
 */
function isResultChanged(key) {
  const orig = form.value.original_result;
  if (!orig) return false;
  return String(orig[key] ?? '') !== String(result.value?.[key] ?? '');
}

/**
 * Builds a list of questions where the client changed the answer compared to the advisor's
 * original submission. Compares original_data.content vs data.content by question ID.
 */
const changedQuestions = computed(() => {
  const origContent = form.value.original_data?.content;
  const currContent = form.value.data?.content;
  if (!origContent || !currContent) return [];

  // Build a map of questionId -> current question object for O(1) lookup
  const currentMap = {};
  for (const fieldset of currContent) {
    for (const q of fieldset.questions ?? []) {
      if (q.id != null) currentMap[q.id] = q;
    }
  }

  // Try to resolve option label for a given value; fall back to raw value
  const resolveLabel = (val, options) => {
    if (val === null || val === undefined) return '-';
    if (options) {
      const opt = options.find(o => String(o.value) === String(val));
      if (opt) return opt.label;
    }
    return String(val);
  };

  const changes = [];
  for (const fieldset of origContent) {
    for (const q of fieldset.questions ?? []) {
      if (q.id == null) continue;
      const current = currentMap[q.id];
      if (!current) continue;
      const origVal = q.value ?? null;
      const newVal = current.value ?? null;
      if (String(origVal) === String(newVal)) continue;
      changes.push({
        id: q.id,
        label: q.label ?? q.id,
        originalValue: resolveLabel(origVal, q.options),
        newValue: resolveLabel(newVal, current.options ?? q.options),
      });
    }
  }
  return changes;
});

</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ t('investmentQuestionnaireModal.investment_questionnaire') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')" id="investmentQuestionnaireForm">
              <div class="modal-body">
                <div v-if="!form.id" class="row mb-3">
                  <div class="form-group">
                    <label for="formTypeSelect" class="form-label">{{ t('investmentQuestionnaireModal.choose_form') }}</label>
                    <select id="formTypeSelect" v-model="selectedForm" class="form-select">
                      <option v-for="form of availableQuestionnaires" :value="form">{{ form.name }}</option>
                    </select>
                  </div>
                </div>
                <div v-if="showContractSelect" class="row mb-3">
                  <div class="form-group">
                    <label for="contractSelect" class="form-label">{{ t('general.contract') }}</label>
                    <select id="contractSelect" v-model="selectedContractId" class="form-select">
                      <option v-for="contract of availableContracts" :key="contract.id" :value="contract.id">
                        {{ contract.contract_number }}
                      </option>
                    </select>
                  </div>
                </div>
                <div v-if="availableQuestionnaires?.length === 0 && !props.disabled" class="alert alert-danger"> {{ t('investmentQuestionnaireModal.no_forms_available') }}</div>
                <DynamicForm :key="(selectedForm?.id ?? 0) + '-' + formKey" v-if="selectedForm" :disabled="props.disabled" :config="selectedForm" ref="dynamicFormRef" :prefill-object="props.prefillObject"></DynamicForm>
              </div>

              <div v-if="selectedForm" class="row mb-3">
                <div class="col-12">
                  <hr/>
                  <fieldset>
                    <legend>{{ t('investmentQuestionnaireModal.result') }}</legend>
                    <table class="table">
                      <thead>
                        <tr>
                          <th scope="col">{{ t('investmentQuestionnaireModal.metric') }}</th>
                          <th scope="col">{{ t('investmentQuestionnaireModal.review') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="key in visibleInvestmentQuestionnaireResultKeys" :key="key" :class="{ 'table-warning': isResultChanged(key) }">
                          <td scope="row">{{ t('investmentQuestionnaireModal.' + key) }}</td>
                          <td>
                            <!-- Show strikethrough original value next to new value when client changed this metric -->
                            <template v-if="isResultChanged(key)">
                              <span class="text-muted text-decoration-line-through me-2 small">{{ translateResultValue(form.original_result?.[key]) }}</span>
                              <span class="text-danger fw-semibold">{{ translateResultValue(result?.[key]) }}</span>
                            </template>
                            <template v-else>
                              {{ translateResultValue(result?.[key]) }}
                            </template>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </fieldset>

                  <!-- List of individual questions changed by the client during onboarding -->
                  <fieldset v-if="changedQuestions.length > 0" class="mt-3">
                    <legend>{{ t('investmentQuestionnaireModal.client_changes') }}</legend>
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th scope="col">{{ t('investmentQuestionnaireModal.question') }}</th>
                          <th scope="col">{{ t('investmentQuestionnaireModal.original_value') }}</th>
                          <th scope="col">{{ t('investmentQuestionnaireModal.new_value') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="change in changedQuestions" :key="change.id">
                          <td>{{ change.label }}</td>
                          <td class="text-muted">{{ change.originalValue }}</td>
                          <td class="text-danger fw-semibold">{{ change.newValue }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </fieldset>
                </div>
              </div>
              <div class="modal-footer">
                <button v-if="!investmentQuestionnaire?.id && !props.disabled" class="btn btn-link text-decoration-underline text-dark" type="button" @click="closeModalWith('cancel')">{{ t('investmentQuestionnaireModal.cancel') }}</button>
                <button v-if="!props.disabled && availableQuestionnaires?.length > 0" :disabled="!selectedForm" class="btn btn-primary btn-md" type="button" @click="countResult()" id="count-button"><span>{{ t('investmentQuestionnaireModal.evaluate') }}</span></button>
                <button v-if="!props.disabled && availableQuestionnaires?.length > 0" :disabled="!selectedForm" class="btn btn-primary btn-md" type="button" @click="closeModalWith('confirm', 'CONCEPT')" id="concept-button">{{ t('investmentQuestionnaireModal.save_as_concept') }}</button>
                <button v-if="!props.disabled && availableQuestionnaires?.length > 0" :disabled="!selectedForm" class="btn btn-primary btn-md" type="submit" id="save-button">{{ t('investmentQuestionnaireModal.save_and_send_to_client') }}</button>
                <button v-if="investmentQuestionnaire?.id" @click="generateDocument" class="btn btn-primary btn-md" type="button" id="generate-document-button"><span v-if="!documentIsGenerating">{{ t('general.generate_document') }}</span><span v-else>{{ t('general.generating') }}</span></button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

